import apiClient from './client';

export type RecommendationPriority = 'high' | 'medium' | 'low';
export type RecommendationStatus   = 'generated' | 'applied' | 'dismissed';
export type RecommendationLevel    = 'high' | 'medium' | 'low';

export interface AiRecommendation {
  id: number;
  audit_run_id: number | null;
  audit_result_id: number | null;
  audit_type: string;
  check_key: string;
  title: string;
  content: string;
  priority: RecommendationPriority;
  priority_label: string;
  impact: RecommendationLevel;
  impact_label: string;
  effort: RecommendationLevel;
  effort_label: string;
  status: RecommendationStatus;
  status_label: string;
  is_actionable: boolean;
  ai_model: string | null;
  cost_usd: number;
  created_at: string | null;
}

export interface PaginatedRecommendations {
  success: boolean;
  data: AiRecommendation[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export interface RecommendationStats {
  total: number;
  by_status: Record<string, number>;
  by_type: Record<string, number>;
  by_priority: Record<string, number>;
  total_cost_usd: number;
}

export interface RecommendationFilters {
  status?: string;
  priority?: string;
  audit_type?: string;
  audit_run_id?: number;
  page?: number;
  per_page?: number;
}

export const recommendationsApi = {
  list(filters: RecommendationFilters = {}): Promise<PaginatedRecommendations> {
    return apiClient.get('/recommendations', { params: filters }).then((r) => r.data);
  },

  find(id: number): Promise<{ success: boolean; data: AiRecommendation }> {
    return apiClient.get(`/recommendations/${id}`).then((r) => r.data);
  },

  stats(): Promise<{ success: boolean; data: RecommendationStats }> {
    return apiClient.get('/recommendations/stats').then((r) => r.data);
  },

  updateStatus(id: number, status: RecommendationStatus): Promise<{ success: boolean; data: AiRecommendation }> {
    return apiClient.patch(`/recommendations/${id}/status`, { status }).then((r) => r.data);
  },

  bulkUpdateStatus(ids: number[], status: RecommendationStatus): Promise<{ success: boolean; data: { updated: number } }> {
    return apiClient.patch('/recommendations/bulk-status', { ids, status }).then((r) => r.data);
  },

  regenerate(auditRunId: number): Promise<{ success: boolean; message: string }> {
    return apiClient.post('/recommendations/regenerate', { audit_run_id: auditRunId }).then((r) => r.data);
  },
};
